import { Callout, Intent, Tooltip } from "@blueprintjs/core";
import { Colors } from "design/colors";
import { Fragment, ReactNode, useCallback, useMemo, useState } from "react";
import { Dialog } from "@blueprintjs/core";
import { ClickableIcon } from "components/ClickableIcon";
import { faCheck, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import slugify from "slugify";
import {
  ControlMode,
  IStartParamDetectRequest,
  IStartParamDetectResponse,
  IWriteControlConfigRequest,
  IWritePositionControlSettingsRequest,
  IWritePowerConfigResponse,
  InputMode,
  PositionResolution,
  PositionType,
  PositionUnits,
} from "utils/common-types";
import {
  Strings,
  downloadData,
  isPolePairCountValid as _isPolePairCountValid,
  strings,
  filenameWithTimestamp,
} from "utils/common";
import {
  IEditableSensorsConfig,
  INonEditableSensorsConfig,
  InputConfig,
  PosCtrlCalResults,
  SensorsConfig,
} from "utils/firestore";
import {
  DEFAULT_AMPS_STEP,
  DEFAULT_RPM_STEP,
  DEFAULT_VOLTS_STEP,
  MIN_RPM_STEP,
  MIN_STEP,
} from "utils/throttle";
import "./Config.css";
import {
  EditorTab,
  InvalidReason,
  getControlConfigFromCStore,
  getInputConfigFromCStore,
  getModificationsToControlConfig,
  getModificationsToInputConfig,
  getModificationsToParamDetect,
  getModificationsToPosCtrl,
  getModificationsToPosCtrlCalResults,
  getModificationsToPowerConfig,
  getModificationsToSensorsConfig,
  getParamDetectFromCStore,
  getPosCtrlCalResultsFromCStore,
  getPosCtrlFromCStore,
  getPowerConfigFromCStore,
  getSensorsConfigFromCStore,
} from "utils/cstore-legacy-mapping";
import { Button } from 'components/Button'
import { InputScreen } from "./InputScreen";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { ParamDetectScreen } from "./ParamDetectScreen";
import { SensorsScreen, sensorsAreValid } from "./SensorsScreen";
import { PowerScreen } from "./PowerScreen";
import { AllSettingsScreen } from "./AllSettingsScreen";
import { SysIDScreen } from "./SysIDScreen";
import { InvalidTabsPopup } from "components/InvalidTabsPopup";
import {
  CStore,
  Op,
  OpType,
} from "cstore/cstore";

export const FORM_WIDTH = 350;

const editorTabs = [
  EditorTab.Throttle,
  EditorTab.Power,
  EditorTab.ParamDetect,
  EditorTab.Sensors,
  EditorTab.SysID,
  EditorTab.AllSettings,
];

export type Edit<T> = (key: keyof T, value: string) => void;

const rowCommonStyle = {
  flex: 1,
  color: Colors.WHITE,
  backgroundColor: Colors.DARK_GREEN,
  margin: 4,
  padding: 8,
  boxShadow: "1px 2px 4px #222222",
};

export const labelStyle = {
  ...rowCommonStyle,
  display: "flex",
  justifyContent: "flex-end",
};

export const valueStyle = {
  ...rowCommonStyle,
};

export const inputStyle = {
  color: Colors.WHITE,
  backgroundColor: Colors.DARK_GREEN,
}

export const configContainerStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  color: Colors.WHITE,
}

export const configModalStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  padding: 30,
  paddingBottom: 10,
  color: Colors.WHITE,
}

export const screenTitleStyle: React.CSSProperties = {
  display: "flex",
  fontSize: 24,
  color: Colors.WHITE,
  fontWeight: "bold",
  marginBottom: 16,
  alignItems: "center",
  flexDirection: "column",
}

export const centerItemsStyle : React.CSSProperties = {
  display: "flex",
  alignContent: "center",
  alignItems: "center",
  justifyContent: "center",
}

export const inputContStyle : React.CSSProperties = {
  width: FORM_WIDTH,
  maxWidth: "100%",
  display: "flex",
  flexDirection: "column",
  marginBottom: 16,
}

export const positionButtonContStyle : React.CSSProperties ={
  display: "flex",
  width: FORM_WIDTH,
  marginBottom: "0.7em",
}

const paramIsNumber = ( s: string ): boolean => {
  return isFinite( parseFloat( s ) );
}

export const paramIsNonNegativeNumber = (s: string): boolean => {
  const n = parseFloat(s);
  return isFinite(n) && n >= 0;
};

export const isPolePairCountValid = ( polePairCount: string | number ): boolean => {
  const ppc = ( typeof polePairCount === 'string' ) ? parseFloat( polePairCount ) : polePairCount;
  return _isPolePairCountValid( ppc );
};

const throttleErrors = ( inputConfig: Strings<InputConfig>, controlConfig: Strings<IWriteControlConfigRequest> ): string[] => {
  type StepSizeInfo = {
    label: string,
    step: number,
    stepStr: string | undefined,
    units: string,
    minStep: number,
  };
  const stepSizes: StepSizeInfo[] = [
    {
      label: 'torque',
      step: parseFloat( inputConfig.ampsStep ?? "" ),
      stepStr: inputConfig.ampsStep,
      units: 'A',
      minStep: MIN_STEP,
    },
    {
      label: 'speed',
      step: parseFloat( inputConfig.rpmStep ?? "" ),
      stepStr: inputConfig.rpmStep,
      units: 'RPM',
      minStep: MIN_RPM_STEP,
    },
  ];

  const createStepSizeErrorStr = ( info: StepSizeInfo ) => {
    const prefix = `Throttle step size for ${info.label} control mode`;
    if ( info.stepStr === "" ) {
      return `${prefix} must be supplied.`
    } else if ( !isFinite( info.step ) ) {
      return `${prefix} is ${info.stepStr} ${info.units} which is invalid.`
    } else {
      return `${prefix} is ${info.stepStr} ${info.units} which is less than minimum of ${info.minStep} ${info.units}.`;
    }
  };

  const isInputModeUSB = ( inputConfig.inputMode === InputMode.USB.toString() );
  let errors;
  const areStepSizesValidated = isInputModeUSB;
  if ( areStepSizesValidated ) {
    errors = (stepSizes
                    .filter(( info: StepSizeInfo ) =>
                      !isFinite( info.step ) || info.step < info.minStep
                    )
                    .map(createStepSizeErrorStr));

    const controlMode: ControlMode = parseInt(controlConfig.controlMode, 10);
    const motorMaxSpeed = parseFloat(controlConfig.motorMaxSpeed); // Call this "Max Throttle Speed" in the UI

    if ( controlMode === ControlMode.SPEED ) {
      if ( !isFinite( motorMaxSpeed ) ) {
        errors.push( "Max Throttle Speed is an invalid value." );
      } else if ( inputConfig.rpmStep !== undefined ) {
        const step = parseFloat( inputConfig.rpmStep );
        if ( isFinite( step ) && motorMaxSpeed < step ) {
          errors.push( "Throttle Step Size cannot exceed Max Throttle Speed" );
        }
      }
    }
  } else {
    errors = [];

    // ToDo: improve errors below
    const { inputMode, inputLimitLower, inputLimitCenter, inputLimitUpper } =
      inputConfig;
    if (
      [inputMode, inputLimitLower, inputLimitCenter, inputLimitUpper].some(
        (v) => v === ""
      )
    ) {
      errors.push("One or more of these are blank: input mode, lower, center, and/or upper input limit.");
    } else if (
      parseInt(inputLimitLower, 10) > parseInt(inputLimitCenter, 10) ||
      parseInt(inputLimitCenter, 10) > parseInt(inputLimitUpper, 10) ||
      parseInt(inputLimitLower, 10) < 0
    ) {
      errors.push("Input limits must be as follows: 0 <= lower <= center <= upper.");
    }
  }

  if ( !isPolePairCountValid( controlConfig.polePairCount ) ) {
    errors.push("Pole pair count must be a positive integer.");
  }

  return errors;
}

// Produce a set of invalid editor tabs.
const getInvalidTabsSet = ( invalidReasons: InvalidReason[] ): Set<EditorTab> => {
    let invalidTabsSet: Set<EditorTab> = new Set();
    for (const invalidReason of invalidReasons) {
        invalidTabsSet.add( invalidReason.tab );
    }
    return invalidTabsSet;
};

export interface INavigationProps {
  setEnableNavigation: (enable: boolean) => void;
  next: (() => void) | undefined;
  prev: (() => void) | undefined;
}

type ParamDetectType = Partial<IStartParamDetectRequest> & IStartParamDetectResponse;

// ToDo: this function is a doozy; reduce it so it fits on 2 screenfuls tops.
export const CStoreEditor = (props: {
  cstore: CStore;
  exit: () => void;
}) => {
  const { cstore, exit } = props;
  const [navigationDisabled, setNavigationDisabled] = useState<boolean>(false);
  const [ warningContents, setWarningContents ] = useState<Map<EditorTab, ReactNode>>( new Map() ); // HACK: this isn't very Reacty

  // These are the originals used to compare against the potentially modified ones.
  const storedParamDetect = useMemo(() => getParamDetectFromCStore(cstore), [cstore]);
  const storedInputConfig = useMemo(() => getInputConfigFromCStore(cstore), [cstore]);
  const storedControlConfig = useMemo(() => getControlConfigFromCStore(cstore), [cstore]);
  const storedPosCtrlSettings = useMemo(() => getPosCtrlFromCStore(cstore), [cstore]);
  const storedPowerConfig = useMemo(() => getPowerConfigFromCStore(cstore), [cstore]);
  const storedSensorsConfig = useMemo(() => getSensorsConfigFromCStore(cstore), [cstore]);
  const storedPosCtrlCalResults = useMemo(() => getPosCtrlCalResultsFromCStore(cstore), [cstore]); // Note: no invalidReasons due to no exceptions

  const invalidTabsSet = useMemo(() => {
    return getInvalidTabsSet([
      ...storedParamDetect.invalidReasons,
      ...storedInputConfig.invalidReasons,
      ...storedControlConfig.invalidReasons,
      ...storedPosCtrlSettings.invalidReasons,
      ...storedPowerConfig.invalidReasons,
      ...storedSensorsConfig.invalidReasons,
    ]);
  }, [
    storedParamDetect,
    storedInputConfig,
    storedControlConfig,
    storedPosCtrlSettings,
    storedPowerConfig,
    storedSensorsConfig,
  ]);
  // Note that AllSettings will never be invalid due to what is loaded from cstore region.
  const initialTabIndex = invalidTabsSet.size ? editorTabs.indexOf(EditorTab.AllSettings) : 0;
  const [currentTabIndex, setCurrentTabIndex] = useState<number>( initialTabIndex );
  const currentTab = editorTabs[currentTabIndex]; // The current tab we are on

  const [manualParamInput, setManualParamInput] = useState<boolean>(false);
  const [paramDetect, setParamDetect] = useState<ParamDetectType>( storedParamDetect );
  const [manualParamDetect, setManualParamDetect] = useState<
    Strings<IStartParamDetectResponse>
  >(
    strings({
      resistance: 0,
      ld: 0,
      lq: 0,
      fluxLinkage: 0,
    })
  );
  const [userSpecifiedParams, setUserSpecifiedParams] =
    useState<Strings<IStartParamDetectRequest>>( strings({ paramDetectMaxCurrent: "", paramDetectMaxRpm: "" }) );

  const [inputConfig, setInputConfig] = useState<Strings<InputConfig>>( strings( storedInputConfig ) );
  const [controlConfig, setControlConfig] = useState<Strings<IWriteControlConfigRequest>>( strings( storedControlConfig));
  const [positionControlSettings, setPositionControlSettings] = useState<Strings<IWritePositionControlSettingsRequest>>( strings( storedPosCtrlSettings));
  const [powerConfig, setPowerConfig] = useState<Strings<IWritePowerConfigResponse>>( strings( storedPowerConfig ) );
  const [editableSensorsConfig, setEditableSensorsConfig] = useState<Strings<IEditableSensorsConfig>>( strings( storedSensorsConfig));
  const [nonEditableSensorsConfig, setNonEditableSensorsConfig] = useState<INonEditableSensorsConfig>( storedSensorsConfig );
  const [posCtrlCalResults, setPosCtrlCalResults] = useState<PosCtrlCalResults | undefined>( storedPosCtrlCalResults );
  const [allSettingsModifyOps, setAllSettingsModifyOps] = useState<Op[]>( [] );

  const manualParamDetectValid = useMemo(() => {
    const { resistance, ld, lq, fluxLinkage } = manualParamDetect;
    if ([resistance, ld, lq, fluxLinkage].some((v) => v === "0")) {
      return false;
    }
    return true;
  }, [manualParamDetect]);
  const paramDetectValid = paramDetect !== undefined || manualParamDetectValid;
  const sensorsValid = useMemo(() => {
      return sensorsAreValid( editableSensorsConfig );
  }, [ editableSensorsConfig ]);

  const inputAndControlConfigsValid = useMemo(() => {
    return (
      throttleErrors( inputConfig, controlConfig ).length === 0
    );
  }, [ inputConfig, controlConfig ]);

  const positionControlSettingsValid = useMemo(() => {
    // if ( controlConfig.controlMode !== ControlMode.POSITION.toString() ) return true;
    const {
      positionControlProportionalGain,
      positionControlIntegralGain,
      positionControlDerivGain,
      positionControlMaxSpeed,
      positionControlMaxAccel,
      positionControlDeadband,
      positionControlType,
      positionUnits,
      positionResolution,
      travelDistance,
    } = positionControlSettings;

    if ( typeof PositionType[ parseInt(positionControlType, 10) ] !== "string" ) return false;
    if ( typeof PositionUnits[ parseInt(positionUnits, 10) ] !== "string" ) return false;
    if ( typeof PositionResolution[ parseInt(positionResolution, 10) ] !== "string" ) return false;

    return paramIsNonNegativeNumber( positionControlProportionalGain ) &&
           paramIsNonNegativeNumber( positionControlIntegralGain ) &&
           paramIsNonNegativeNumber( positionControlDerivGain ) &&
           paramIsNonNegativeNumber( positionControlMaxSpeed ) &&
           paramIsNonNegativeNumber( positionControlMaxAccel ) &&
           paramIsNonNegativeNumber( positionControlDeadband ) &&
           paramIsNonNegativeNumber( travelDistance );
  }, [ positionControlSettings ]);

  const powerConfigValid = useMemo(() => {
    const {
      batteryCurrentLimit,
      motorCurrentLimit,
      overtemperatureThreshold,
      criticalOvervoltageThreshold,
      criticalUndervoltageThreshold,
      warningOvervoltageThreshold,
      warningUndervoltageThreshold,
      warningMotorCurrentThreshold,
      criticalMotorCurrentThreshold,
    } = powerConfig;
    const thresholds = [
      overtemperatureThreshold,
      criticalOvervoltageThreshold,
      criticalUndervoltageThreshold,
      warningOvervoltageThreshold,
      warningUndervoltageThreshold,
      warningMotorCurrentThreshold,
      criticalMotorCurrentThreshold,
    ];
    if ( [batteryCurrentLimit, motorCurrentLimit, ...thresholds].some( (v) => !paramIsNonNegativeNumber( v ) ) ) {
      return false;
    }
    if ( thresholds.some( (v) => !paramIsNumber( v ) ) ) {
      return false;
    }
    if (
      parseFloat(warningOvervoltageThreshold) >= parseFloat(criticalOvervoltageThreshold) ||
      parseFloat(warningUndervoltageThreshold) >= parseFloat(warningOvervoltageThreshold) ||
      parseFloat(warningUndervoltageThreshold) <= parseFloat(criticalUndervoltageThreshold) ||
      parseFloat(warningMotorCurrentThreshold) >= parseFloat(criticalMotorCurrentThreshold)
    ) {
      return false;
    }
    return true;
  }, [powerConfig]);

  const allInvalidReasons: InvalidReason[] = [
    ...storedParamDetect.invalidReasons,
    ...storedInputConfig.invalidReasons,
    ...storedControlConfig.invalidReasons,
    ...storedPosCtrlSettings.invalidReasons,
    ...storedPowerConfig.invalidReasons,
    ...storedSensorsConfig.invalidReasons,
  ];


  const tabs = useMemo(() => {
    return editorTabs.map((tab, index) => {
      let isValid: boolean, title: string;
      switch (tab) {
        case EditorTab.Throttle:
          isValid = inputAndControlConfigsValid;
          title = "Throttle";
          break;
        case EditorTab.Power:
          isValid = powerConfigValid;
          title = "Limits";
          break;
        case EditorTab.ParamDetect:
          isValid = paramDetectValid;
          title = "Parameters";
          break;
        case EditorTab.Sensors:
          isValid = sensorsValid;
          title = "Sensors";
          break;
        case EditorTab.SysID:
          isValid = true; // SysID has no validation requirements
          title = "System ID";
          break;
        case EditorTab.AllSettings:
          isValid = true; // Note: no validation here
          title = "All Settings";
          break;
      }
      const cstoreInvalid = invalidTabsSet.has(tab);

      let tooltipText = "";
      if ( cstoreInvalid ) tooltipText = "The cstore is invalid for this tab"; // TODO: convert invalidTabsSet to a map, with values being the messages, and show here

      return (
        <Fragment key={tab.toString() + "-tab"}>
          <div
            style={{
              flex: 1,
              display: "flex",
              fontSize: 16,
              color: Colors.WHITE,
              backgroundColor: isValid
                ? Colors.BUTTON_GREEN
                : Colors.DARK_GREEN,
              
              opacity: tab === currentTab ? 1 : 0.5,
              justifyContent: "center",
              alignItems: "center",
              padding: 8,
              cursor: navigationDisabled ? "not-allowed" : "pointer",
            }}
            onClick={
              ( navigationDisabled || cstoreInvalid ) ? undefined : () => setCurrentTabIndex(index)
            }
          >
            { tooltipText && (
              <Tooltip content={ tooltipText }>
                {title}
              </Tooltip>
            ) || title }
            {isValid ? (
              <FontAwesomeIcon
                style={{ marginLeft: 4 }}
                icon={faCheck}
                color={Colors.WHITE}
              />
            ) : null}
            
            {!isValid && cstoreInvalid && (
              <FontAwesomeIcon
                style={{ marginLeft: 4 }}
                icon={faTriangleExclamation}
                color="#FF4136"
              />
            )}
          </div>
          {index === editorTabs.length - 1 ? null : (
            <div
              style={{ height: "100%", width: 1, backgroundColor: "#000000" }}
            />
          )}
        </Fragment>
      );
    });
  }, [
    inputAndControlConfigsValid,
    powerConfigValid,
    currentTab,
    navigationDisabled,
    paramDetectValid,
    sensorsValid,
    invalidTabsSet,
  ]);

  const lastIndex = editorTabs.length - 1;
  const next =
    navigationDisabled || currentTabIndex === lastIndex
      ? undefined
      : () => setCurrentTabIndex((v) => v + 1);
  const prev =
    navigationDisabled || currentTabIndex === 0
      ? undefined
      : () => setCurrentTabIndex((v) => v - 1);

  const navigationProps: INavigationProps = {
    setEnableNavigation: (v: boolean) => setNavigationDisabled(!v),
    next,
    prev,
  };

  const editInputConfig: Edit<InputConfig> = (key, value) =>
    setInputConfig((c) => ({ ...c, [key]: value }));
  const editControlConfig: Edit<IWriteControlConfigRequest> = (key, value) =>
    setControlConfig((c) => ({ ...c, [key]: value }));
  const editPositionControlSettings: Edit<IWritePositionControlSettingsRequest> = (key, value) =>
    setPositionControlSettings((c) => ({ ...c, [key]: value }));
  const editPowerConfig: Edit<IWritePowerConfigResponse> = (key, value) =>
    setPowerConfig((c) => ({ ...c, [key]: value }));
  const editManualParamConfig: Edit<IStartParamDetectResponse> = (key, value) =>
    setManualParamDetect((c) => ({ ...c, [key]: value }));
  const editUserSpecifiedParams: Edit<IStartParamDetectRequest> = (key, value) =>
    setUserSpecifiedParams((c) => ({ ...c, [key]: value }));
  // Note: below is only for user-editable sensors config
  const editSensorsConfig: Edit<SensorsConfig> = (key, value) =>
    setEditableSensorsConfig((c) => ({ ...c, [key]: value }));

  const paramDetectModifyOps: Op[] = useMemo(() => {
    if ( invalidTabsSet.has( EditorTab.ParamDetect ) ) return [];
    let newPD: IStartParamDetectResponse = paramDetect;
    if ( manualParamInput ) {
      newPD = {
          resistance:  parseFloat(manualParamDetect.resistance),
          ld:          parseFloat(manualParamDetect.ld),
          lq:          parseFloat(manualParamDetect.lq),
          fluxLinkage: parseFloat(manualParamDetect.fluxLinkage),
      };
      if ( !isFinite( newPD.resistance ) || !isFinite( newPD.ld ) || !isFinite( newPD.lq ) || !isFinite( newPD.fluxLinkage ) ) {
        console.info( "one or more param detect values are non-finite" );
        return [];
      }
    }
    return getModificationsToParamDetect( storedParamDetect, newPD );
  }, [
    paramDetect,
    storedParamDetect,
    manualParamInput,
    manualParamDetect,
    invalidTabsSet,
  ] );

  const inputConfigModifyOps: Op[] = useMemo(() => {
    if ( invalidTabsSet.has( EditorTab.Throttle ) ) return [];
    const newCfg = {
      inputMode: parseInt(inputConfig.inputMode, 10),
      inputLimitLower: parseInt(inputConfig.inputLimitLower, 10),
      inputLimitCenter: parseInt(inputConfig.inputLimitCenter, 10),
      inputLimitUpper: parseInt(inputConfig.inputLimitUpper, 10),
      alwaysOn: parseInt(inputConfig.alwaysOn, 10),
      rpmStep: ( inputConfig.rpmStep !== undefined ) ? parseFloat( inputConfig.rpmStep ) : DEFAULT_RPM_STEP,
      ampsStep: ( inputConfig.ampsStep !== undefined ) ? parseFloat( inputConfig.ampsStep ) : DEFAULT_AMPS_STEP,
      voltsStep: ( inputConfig.voltsStep !== undefined ) ? parseFloat( inputConfig.voltsStep ) : DEFAULT_VOLTS_STEP,
    };
    let foundNonFinite = false;
    for (const [k,v] of Object.entries(newCfg)) {
      if ( !isFinite(v) && k !== "alwaysOn" ) {
        console.info( `input config field ${ k } has non-finite value ${ v }` );
        foundNonFinite = true;
      }
    }
    if ( foundNonFinite ) return [];
    return getModificationsToInputConfig( storedInputConfig, newCfg );
  }, [
    storedInputConfig,
    inputConfig,
    invalidTabsSet,
  ] );

  const controlConfigModifyOps: Op[] = useMemo(() => {
    if ( invalidTabsSet.has( EditorTab.Throttle ) ) return [];
    const newCfg = {
      motorMaxSpeed:                parseFloat(controlConfig.motorMaxSpeed),
      polePairCount:                parseFloat(controlConfig.polePairCount),
      speedControlProportionalGain: parseFloat( controlConfig.speedControlProportionalGain ),
      speedControlIntegralGain:     parseFloat( controlConfig.speedControlIntegralGain ),
      controlMode:                  parseInt(controlConfig.controlMode, 10),
      mtpa:                         parseInt(controlConfig.mtpa, 10),
      fluxWeakening:                parseInt(controlConfig.fluxWeakening, 10),
      deprecatedPositionControlProportionalGain: -1,
    };
    let foundNonFinite = false;
    for (const [k,v] of Object.entries(newCfg)) {
      if (!isFinite(v)) {
        console.info( `control config field ${ k } has non-finite value ${ v }` );
        foundNonFinite = true;
      }
    }
    if ( foundNonFinite ) return [];
    return getModificationsToControlConfig( storedControlConfig, newCfg );
  }, [
    storedControlConfig,
    controlConfig,
    invalidTabsSet,
  ] );

  const posCtrlModifyOps: Op[] = useMemo(() => {
    if ( invalidTabsSet.has( EditorTab.Throttle ) ) return [];
    const newCfg = {
      positionControlProportionalGain: parseFloat(positionControlSettings.positionControlProportionalGain),
      positionControlIntegralGain:     parseFloat(positionControlSettings.positionControlIntegralGain),
      positionControlDerivGain:        parseFloat(positionControlSettings.positionControlDerivGain),
      positionControlMaxSpeed:         parseFloat(positionControlSettings.positionControlMaxSpeed),
      positionControlMaxAccel:         parseFloat(positionControlSettings.positionControlMaxAccel),
      positionControlDeadband:         parseFloat(positionControlSettings.positionControlDeadband),
      positionControlType:             parseInt(positionControlSettings.positionControlType, 10),
      positionUnits:                   parseInt(positionControlSettings.positionUnits, 10),
      positionResolution:              parseInt(positionControlSettings.positionResolution, 10),
      travelDistance:                  parseFloat(positionControlSettings.travelDistance),
    };
    let foundNonFinite = false;
    for (const [k,v] of Object.entries(newCfg)) {
      if (!isFinite(v)) {
        console.info( `position control config field ${ k } has non-finite value ${ v }` );
        foundNonFinite = true;
      }
    }
    if ( foundNonFinite ) return [];
    return getModificationsToPosCtrl( storedPosCtrlSettings, newCfg );
  }, [
    positionControlSettings,
    storedPosCtrlSettings,
    invalidTabsSet,
  ] );

  const posCtrlCalModifyOps: Op[] = useMemo(() => {
    // Note: no invalidTabsSet guard here — calibration results come from
    // hardware operations (sensor zeroing) and must always be exportable,
    // even when other Throttle tab fields are invalid in CStore.
    if ( posCtrlCalResults === undefined ) return []; // Cannot remove calibration results from cstore
    return getModificationsToPosCtrlCalResults( storedPosCtrlCalResults, posCtrlCalResults );
  }, [
    posCtrlCalResults,
    storedPosCtrlCalResults,
  ] );

  const powerConfigModifyOps: Op[] = useMemo(() => {
    if ( invalidTabsSet.has( EditorTab.Power ) ) return [];
    const newCfg = {
      batteryCurrentLimit: parseFloat(powerConfig.batteryCurrentLimit),
      motorCurrentLimit: parseFloat(powerConfig.motorCurrentLimit),
      overtemperatureThreshold: parseFloat(
        powerConfig.overtemperatureThreshold
      ),
      criticalOvervoltageThreshold: parseFloat(powerConfig.criticalOvervoltageThreshold),
      criticalUndervoltageThreshold: parseFloat(powerConfig.criticalUndervoltageThreshold),
      overcurrentThresholdDeprecated: 0,
      warningOvervoltageThreshold: parseFloat(powerConfig.warningOvervoltageThreshold),
      warningUndervoltageThreshold: parseFloat(powerConfig.warningUndervoltageThreshold),
      warningMotorCurrentThreshold: parseFloat(powerConfig.warningMotorCurrentThreshold),
      criticalMotorCurrentThreshold: parseFloat(powerConfig.criticalMotorCurrentThreshold),
    };
    let foundNonFinite = false;
    for (const [k,v] of Object.entries(newCfg)) {
      if (!isFinite(v)) {
        console.info( `power config field ${ k } has non-finite value ${ v }` );
        foundNonFinite = true;
      }
    }
    if ( foundNonFinite ) return [];
    return getModificationsToPowerConfig( storedPowerConfig, newCfg );
  }, [
    storedPowerConfig,
    powerConfig,
    invalidTabsSet,
  ] );

  const sensorsConfigModifyOps: Op[] = useMemo(() => {
    if ( invalidTabsSet.has( EditorTab.Sensors ) ) return [];
    const newCfg = {
      ...nonEditableSensorsConfig,
      sensorMode: parseInt(editableSensorsConfig.sensorMode, 10),
      encoderCpr: parseInt(editableSensorsConfig.encoderCpr, 10),
      accelerationRamp_mkradps2: parseFloat(editableSensorsConfig.accelerationRamp_mkradps2), // mech. rad/s^2
    };
    // Only check editable sensor fields for non-finite
    if ( !isFinite( newCfg.sensorMode ) || !isFinite( newCfg.encoderCpr ) || !isFinite( newCfg.accelerationRamp_mkradps2 ) ) {
      return [];
    }

    return getModificationsToSensorsConfig(
      storedSensorsConfig,
      newCfg,
    );
  }, [
    storedSensorsConfig,
    editableSensorsConfig,
    nonEditableSensorsConfig,
    invalidTabsSet,
  ] );

  const modifyOps: Op[] = useMemo(() => [
      ...paramDetectModifyOps,
      ...inputConfigModifyOps,
      ...controlConfigModifyOps,
      ...posCtrlModifyOps,
      ...posCtrlCalModifyOps,
      ...powerConfigModifyOps,
      ...sensorsConfigModifyOps,
      ...allSettingsModifyOps,
  ], [
      paramDetectModifyOps,
      inputConfigModifyOps,
      controlConfigModifyOps,
      posCtrlModifyOps,
      posCtrlCalModifyOps,
      powerConfigModifyOps,
      sensorsConfigModifyOps,
      allSettingsModifyOps,
  ] );

  // Modal state for export
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportComment, setExportComment] = useState("");

  const handleExportClick = () => {
    setShowExportModal(true);
  };

  const handleExportCancel = () => {
    setShowExportModal(false);
    setExportComment("");
  };

  const handleExportConfirm = () => {
    try {
      // Update build date
      const d = new Date();
      const updateHeaderOp: Op = {
        type: OpType.UpdateHeader,
        updates: { buildYYYYMMDD: [ d.getFullYear(), d.getMonth() + 1, d.getDate() ] }
      }; 
      setShowExportModal(false);
      setExportComment(""); 
      
      const slugifiedString = slugify(exportComment, {
        lower: true, // Convert to lowercase
        strict: true // Remove special characters
      });
      const updateCommentOp: Op = {
        type: OpType.UpsertTag,
        id:"UserComment",
        value: exportComment,
      }; 
      let newCstore: CStore;
      if (exportComment) {
        newCstore = cstore.modify([
          ...modifyOps,
          updateHeaderOp,
          updateCommentOp,
        ]);
      } else {
        newCstore = cstore.modify([
          ...modifyOps,
          updateHeaderOp,
        ]);
      }
      const data = newCstore.export();

      let fileName;
      if (slugifiedString) {
        fileName = `${filenameWithTimestamp("exported-cstore")}-${slugifiedString}.bin`;
      } else {
        fileName = `${filenameWithTimestamp("exported-cstore")}.bin`;
      }
      downloadData(fileName, data);

    } catch (e) {
      console.warn("Export Config", e);
    }
  };

  const setWarningContentsCurrentTab = useCallback(( contents: ReactNode ) => {
    setWarningContents( ( oldContentsMap: Map<EditorTab, ReactNode> ) => {
      if ( oldContentsMap.get( currentTab ) === contents ) {
        // Unchanged, exit early
        return oldContentsMap;
      }
      const newContentsMap = new Map( oldContentsMap ); // Clone map
      newContentsMap.set( currentTab, contents );
      return newContentsMap;
    } );
  }, [ currentTab, setWarningContents ]);

  let content;
  switch (currentTab) {
    case EditorTab.Throttle:
      content = (
        <InputScreen
          {...navigationProps}
          inputConfig={inputConfig}
          powerConfig={powerConfig}
          editInputConfig={editInputConfig}
          controlConfig={controlConfig}
          positionControlSettings={positionControlSettings}
          posCtrlCalResultsStorable={!!storedPosCtrlCalResults} // Indicates whether the fields for storing calibration results are in cstore
          setPosCtrlCalResults={setPosCtrlCalResults}
          editControlConfig={editControlConfig}
          editPositionControlSettings={editPositionControlSettings}
          setWarningContents={setWarningContentsCurrentTab}
        />
      );
      break;
    case EditorTab.Power:
      content = (
        <PowerScreen
          {...navigationProps}
          powerConfig={powerConfig}
          powerConfigValid={powerConfigValid}
          editPowerConfig={editPowerConfig}
        />
      );
      break;
    case EditorTab.ParamDetect:
      content = (
        <ParamDetectScreen
          {...navigationProps}
          paramDetect={paramDetect}
          setParamDetect={setParamDetect}
          setManualParamInput={setManualParamInput}
          manualParamInput={manualParamInput}
          manualParamDetect={manualParamDetect}
          editManualParamConfig={editManualParamConfig}
          userSpecifiedParams={userSpecifiedParams}
          editUserSpecifiedParams={editUserSpecifiedParams}
          polePairCount={parseFloat(controlConfig.polePairCount)}
        />
      );
      break;
    case EditorTab.Sensors:
      content = (
        <SensorsScreen
          {...navigationProps}
          storedSensorsConfig={storedSensorsConfig}
          editableSensorsConfig={editableSensorsConfig}
          editSensorsConfig={editSensorsConfig}
          nonEditableSensorsConfig={nonEditableSensorsConfig}
          setNonEditableSensorsConfig={setNonEditableSensorsConfig}
          setWarningContents={setWarningContentsCurrentTab}
        />
      );
      break;
    case EditorTab.SysID:
      content = (
        <SysIDScreen
          {...navigationProps}
        />
      );
      break;
    case EditorTab.AllSettings:
      content = (
        <AllSettingsScreen
          {...navigationProps}
          cstore={cstore}
          setAllSettingsModifyOps={setAllSettingsModifyOps}
        />
      );
      break;
  }


  // We can still export if a tab is invalid due to its cstore fields being
  // invalid (no changes will be added to modifyOps for those).
  let exportDisabled = false;
  if ( !paramDetectValid && !invalidTabsSet.has( EditorTab.ParamDetect ) ) {
    exportDisabled ||= true;
  }
  if ( !inputAndControlConfigsValid && !invalidTabsSet.has( EditorTab.Throttle ) ) {
    exportDisabled ||= true;
  }
  if ( !positionControlSettingsValid && !invalidTabsSet.has( EditorTab.Throttle ) ) {
    exportDisabled ||= true;
  }
  if ( !powerConfigValid && !invalidTabsSet.has( EditorTab.Power ) ) {
    exportDisabled ||= true;
  }
  if ( !sensorsValid && !invalidTabsSet.has( EditorTab.Sensors ) ) {
    exportDisabled ||= true;
  }
  const [showInvalidPopup, setShowInvalidPopup] = useState(true);
  const warningContentsThisTab = warningContents.get(currentTab);
  return (
    <div
      style={{
        display: "flex",
        flex: 1,
        flexDirection: "column",
        overflow: "auto",
      }}
    >
      <div style={{ display: "flex", marginBottom: 32 }}>{tabs}</div>
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        <div style={{ position: "absolute", top: -16, left: 32 }}>
          <ClickableIcon
            icon={faXmark}
            color={Colors.WHITE}
            onClick={exit}
            size="2x"
            style={{ padding: 8 }}
            disabled={navigationDisabled}
          />
        </div>
        {content}
        <div
          style={{ display: "flex", justifyContent: "center", marginTop: 16 }}
        >
          <Button
            style={{ width: FORM_WIDTH }}
            backgroundColor={Colors.BUTTON_GREEN}
            textColor={Colors.WHITE}
            onClick={handleExportClick}
            disabled={ exportDisabled || navigationDisabled }
          >
            <span style={{ fontWeight: modifyOps.length ? "bold" : "normal" }}>
              Export
              { modifyOps.length ? ` (${ modifyOps.length })` : undefined }
            </span>
          </Button>
          {/* Export Dialog */}
          <Dialog
            isOpen={showExportModal}
            onClose={handleExportCancel}
            title="Export Configuration"
            style={{ backgroundColor: Colors.DARK_GREEN, color: Colors.WHITE } }
          >
            <div style={{ padding: 24, minWidth: 350, backgroundColor: Colors.DARK_GREEN }}>
              <div style={{ margin: "16px 0" }}>
                <label htmlFor="export-comment" style={{ color: Colors.WHITE }}>
                  Comment about this file:
                </label>
                <textarea
                  id="export-comment"
                  value={exportComment}
                  onChange={e => setExportComment(e.target.value)}
                  style={{
                    ...inputStyle,
                    width: "100%",
                    minHeight: 60,
                    marginTop: 8,
                    border: `1px solid ${Colors.BUTTON_GREEN}`,
                    borderRadius: 4,
                    padding: 8,
                    resize: "vertical",
                    boxSizing: "border-box",
                  }}
                  placeholder="Enter a comment for this export (optional)"
                />
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 24 }}>
                <Button
                  onClick={handleExportCancel}
                  backgroundColor={Colors.DARK_GREEN}
                  textColor={Colors.WHITE}
                  style={{ border: `1px solid ${Colors.WHITE}` }}
                >
                  Cancel
                </Button>
                <Button onClick={handleExportConfirm} backgroundColor={Colors.BUTTON_GREEN} textColor={Colors.WHITE}>
                  Export
                </Button>
              </div>
            </div>
          </Dialog>
        </div>
        <div
          style= {{
            display: "flex",
            flex: 1,
            flexDirection: "column",
            alignItems: "center",
            marginTop: 16,
            visibility: warningContentsThisTab ? "visible" : "hidden",
          }}
        >
          <div style={{ flex: 1, width: 1.35 * FORM_WIDTH }}>
            <Callout intent={ Intent.WARNING } >
              {warningContentsThisTab}
            </Callout>
          </div>
        </div>
        {showInvalidPopup && (
          <InvalidTabsPopup
            invalidReasons={ allInvalidReasons}
            onClose={() => setShowInvalidPopup(false)}
            autoHideDuration={5000}
          />
        )}
      </div>
    </div>
  );
};
